import React, { useMemo, useCallback } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import ThemeableAgGrid from "../../common/components/agGrid/ThemeableAgGrid";
import { useTranslation } from "react-i18next";
import _ from "lodash";
import Loading from "../../common/components/loading/loading";

const AbStorageSummaryTable = ({ summaryData, isLoading }) => {
  const { t } = useTranslation(["marketdata"]);

  // Define cell renderer for change values
  const renderChangeCell = useCallback((params) => {
    const isPositive = params.value >= 0;
    return (
      <span
        style={{
          display: "inline-block",
          color: isPositive ? "var(--theme-green)" : "var(--theme-red)",
        }}
      >
        <FontAwesomeIcon
          icon={isPositive ? faSortUp : faSortDown}
          style={{
            fontSize: "1em",
            height: "1em",
            marginRight: 4,
          }}
        />{" "}
        {params.value}
      </span>
    );
  }, []);

  // Memoize column definitions
  const columnDefs = useMemo(
    () => [
      {
        field: "item",
        headerName: t("Item"),
        cellClass: "table-bold",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 65,
        resizable: true,
      },
      {
        field: "date",
        headerName: t("Date"),
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 67,
        resizable: true,
      },
      {
        field: "inventory",
        headerName: t("Inventory"),
        type: "numericColumn",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 63,
        resizable: true,
      },
      {
        field: "dailyChange",
        headerName: t("DailyChange"),
        type: "numericColumn",
        cellRenderer: renderChangeCell,
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 65,
        resizable: true,
      },
      {
        field: "percentFull",
        headerName: t("PercentFull"),
        type: "numericColumn",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 65,
        resizable: true,
      },
      {
        field: "capacity",
        headerName: t("Capacity"),
        type: "numericColumn",
        sortable: true,
        filter: false,
        flex: 1,
        minWidth: 65,
        resizable: true,
      },
    ],
    [t, renderChangeCell],
  );

  // Default column properties
  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: false,
    }),
    [],
  );

  // Map to view model
  const mapToViewModel = useCallback((summaryData) => {
    const dateFormatOptions = {
      year: "numeric",
      month: "2-digit",
      day: "numeric",
    };

    return summaryData.map((data) => ({
      item: data.item,
      date: new Date(data.gasDay).toLocaleDateString(
        "en-US",
        dateFormatOptions,
      ),
      inventory: data.inventory.toFixed(1),
      dailyChange: data.dailyChange,
      percentFull: data.percentFull.toFixed(1) + "%",
      capacity: data.capacity,
    }));
  }, []);

  // Create a getRowId function to ensure stable row identification
  const getRowId = useCallback((params) => params.data.item, []);

  if (isLoading) return <Loading />;

  const tableData = _.orderBy(mapToViewModel(summaryData), "sortOrder", "asc");

  return (
    <div>
      <div className="row">
        <div className="col-md-12">
          <h6>AB Natural Gas Storage (Bcf)</h6>
        </div>
      </div>
      <ThemeableAgGrid
        style={{ whiteSpace: "normal" }}
        className="table-extra-small ab-storage-table"
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={tableData}
        getRowId={getRowId}
        animateRows={true}
        domLayout="autoHeight"
        allowHorizontalScroll={false}
      />
    </div>
  );
};

export default AbStorageSummaryTable;
